import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useAuth } from "@/contexts/AuthContext";
import { useCreditBalance } from "@/hooks/useCreditBalance";
import { Coins, Globe, ChevronDown, LogOut, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";

export function AppHeader() {
  const { workspace, workspaces, switchWorkspace, unlockedRegions, allRegions, isAdmin } = useWorkspace();
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { data: creditBalance, isLoading: creditsLoading, isError: creditsError } = useCreditBalance(workspace?.id);

  // Color thresholds only apply when we have a real value. While loading or on
  // error, fall through to the muted-foreground style so we don't render "0 in
  // red" and make the user think their credits are exhausted.
  const creditsResolved = !creditsLoading && !creditsError && creditBalance != null;
  // Threshold matches free-tier monthly allocation (1000 credits, see tier_configs.free).
  const creditPercent = creditsResolved ? (creditBalance / 1000) * 100 : 100;

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  return (
    <header className="h-14 border-b border-border px-6 flex items-center justify-between shrink-0">
      {/* Workspace Switcher */}
      <div data-tour="workspace-switcher" className="flex items-center gap-4">
        {workspaces.length > 1 ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="font-medium gap-1">
                {workspace?.name || "Select Workspace"}
                <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {workspaces.map((ws) => (
                <DropdownMenuItem
                  key={ws.id}
                  onClick={() => switchWorkspace(ws.id)}
                  className={cn(ws.id === workspace?.id && "bg-accent")}
                >
                  {ws.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <span className="text-sm font-medium">{workspace?.name}</span>
        )}
      </div>

      {/* Right side: Credits, Regions, User */}
      <div className="flex items-center gap-4">
        {/* Credits indicator */}
        <div className="flex items-center gap-2 text-sm">
          <Coins className="w-4 h-4 text-muted-foreground" />
          <span className={cn(
            "font-medium",
            creditPercent < 20 ? "text-destructive" :
            creditPercent < 50 ? "text-yellow-500" :
            "text-muted-foreground"
          )}>
            {creditsResolved ? creditBalance.toLocaleString() : "—"}
          </span>
          <span className="text-muted-foreground">credits</span>
        </div>

        {/* Region count */}
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <Globe className="w-4 h-4" />
          <span>{unlockedRegions.length}/{allRegions.length}</span>
        </div>

        {/* User menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="w-8 h-8 rounded-full">
              <User className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <div className="px-2 py-1.5">
              <p className="text-sm font-medium">{user?.email}</p>
              <p className="text-xs text-muted-foreground capitalize">{isAdmin ? "Admin" : "Member"}</p>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/app/settings")}>
              Workspace Settings
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleSignOut}>
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
